import { types as sdkTypes } from './sdkLoader';
import config from '../config';

const { Money } = sdkTypes;

export const PRICING_MODEL_HOURLY = 'hourly';
export const PRICING_MODEL_MONTHLY = 'monthly';
export const PRICING_MODEL_PROJECT = 'project';
export const PRICING_MODELS = [PRICING_MODEL_HOURLY, PRICING_MODEL_MONTHLY, PRICING_MODEL_PROJECT];

export const MAX_PACKAGES = 3;
export const MAX_FAQ_ITEMS = 5;
export const MAX_LISTING_IMAGES = 3;
export const LISTING_TITLE_MAX_LENGTH = 60;
export const DESCRIPTION_MIN_LENGTH = 300;
export const DESCRIPTION_MAX_LENGTH = 5000;
export const PACKAGE_TITLE_MAX_LENGTH = 60;
export const PACKAGE_DESCRIPTION_MAX_LENGTH = 5000;
export const FAQ_QUESTION_MAX_LENGTH = 150;
export const FAQ_ANSWER_MAX_LENGTH = 2000;

/**
 * @typedef {{ pricingModel: string, price: number, title: string, description: string }} ListingPackage
 * @typedef {{ question: string, answer: string }} FaqItem
 * @typedef {{ title: string, pricingModel: string, price: { amount: number, currency: string } }} EnquiryPackage
 */

const LEGACY_PACKAGE_COUNT = 5;

const publicDataOf = listing =>
  (listing && listing.attributes && listing.attributes.publicData) || {};

const legacyPackage = (publicData, n, price) => ({
  pricingModel: PRICING_MODEL_PROJECT,
  price,
  title: publicData[`package${n}Name`] || '',
  description: publicData[`package${n}Description`] || '',
});

export const getListingPackages = listing => {
  const publicData = publicDataOf(listing);
  if (Array.isArray(publicData.packages)) {
    return publicData.packages;
  }
  const result = [];
  const basePrice = listing && listing.attributes && listing.attributes.price;
  if (basePrice) {
    result.push(legacyPackage(publicData, 1, basePrice.amount));
  }
  for (let n = 2; n <= LEGACY_PACKAGE_COUNT; n++) {
    const price = publicData[`package${n}Price`];
    if (publicData[`package${n}Name`] && price) {
      result.push(legacyPackage(publicData, n, price));
    }
  }
  return result;
};

export const getListingFaq = listing => {
  const { faq } = publicDataOf(listing);
  return Array.isArray(faq) ? faq : [];
};

// publicData is author-written: the listing page (and its JSON-LD) skips a
// package whose price or model can't be shown (the old page also skipped
// non-integer legacy prices) and an FAQ item without a question.
export const isShowablePackage = pkg =>
  !!pkg && Number.isInteger(pkg.price) && PRICING_MODELS.includes(pkg.pricingModel);

export const isShowableFaqItem = item =>
  !!item && typeof item.question === 'string' && !!item.question.trim();

export const getListingFromPrice = listing => {
  const publicData = publicDataOf(listing);
  const [first] = getListingPackages(listing);
  if (Array.isArray(publicData.packages) && first) {
    return { price: new Money(first.price, config.currency), pricingModel: first.pricingModel };
  }
  const legacy = Array.isArray(publicData.priceArray) && publicData.priceArray[0];
  if (legacy && legacy._packagePrice) {
    return {
      price: new Money(legacy._packagePrice, config.currency),
      pricingModel: PRICING_MODEL_PROJECT,
    };
  }
  const price = (listing && listing.attributes && listing.attributes.price) || null;
  return { price, pricingModel: PRICING_MODEL_PROJECT };
};

const UNIT_MESSAGE_IDS = {
  [PRICING_MODEL_HOURLY]: 'ListingPackages.unitHourly',
  [PRICING_MODEL_MONTHLY]: 'ListingPackages.unitMonthly',
  [PRICING_MODEL_PROJECT]: 'ListingPackages.unitProject',
};

export const priceUnitMessageId = pricingModel =>
  UNIT_MESSAGE_IDS[pricingModel] || UNIT_MESSAGE_IDS[PRICING_MODEL_PROJECT];

/**
 * The "from {price}" figure of a service card: package 1's price as plain
 * { amount, currency } for PackagePrice and its pricing model. publicData is
 * author-written, so an unknown model reads as project. Null without a usable
 * price.
 */
export const getCardPrice = listing => {
  const { price, pricingModel: rawPricingModel } = getListingFromPrice(listing);
  if (!price || !Number.isInteger(price.amount) || !price.currency) {
    return null;
  }
  const pricingModel = PRICING_MODELS.includes(rawPricingModel)
    ? rawPricingModel
    : PRICING_MODEL_PROJECT;
  return {
    price: { amount: price.amount, currency: price.currency },
    pricingModel,
  };
};

export const buildPackagesSaveData = packages => ({
  price: new Money(packages[0].price, config.currency),
  publicData: { packages },
});

export const toEnquiryPackage = pkg => ({
  title: pkg.title,
  pricingModel: pkg.pricingModel,
  price: { amount: pkg.price, currency: config.currency },
});

// protectedData.enquiryPackage is written client-side by the customer, so it is
// untrusted: only render it when every field has the expected shape.
export const isValidEnquiryPackage = pkg =>
  !!pkg &&
  typeof pkg === 'object' &&
  typeof pkg.title === 'string' &&
  pkg.title.length > 0 &&
  PRICING_MODELS.includes(pkg.pricingModel) &&
  !!pkg.price &&
  typeof pkg.price === 'object' &&
  Number.isInteger(pkg.price.amount) &&
  pkg.price.currency === config.currency;

export const hourlyTakenByOther = (packages, editingIndex) =>
  packages.some((pkg, i) => i !== editingIndex && pkg.pricingModel === PRICING_MODEL_HOURLY);
